<script lang="ts">
    import KpFavouritesButton from '../kp-favourites-button/KpFavouritesButton.svelte';
    import type {Record, RichRecord} from '../../../typings/portaro.be.types';
    import {disabledWhen} from '../../../svelte/svelte-actions/disabled-when';
    import type {Injector, LocalizeFunction} from '../../../svelte/types';
    import {getContext, onDestroy, onMount,} from 'svelte';
    import {CONTEXT_PATH_KEY, INJECTOR_KEY, LOCALIZE_KEY} from '../../../svelte/utils';
    import KpCover from '../../../record/kp-cover/KpCover.svelte';
    import KpLoadingBlock from '../kp-loading/KpLoadingBlock.svelte';
    import {KpFavouritesPanelPresenter} from './kp-favourites-panel.presenter';
    import type {State} from '../../constants/portaro.constants';
    import {states} from '../../constants/portaro.constants';
    import KpLoadingInline from '../kp-loading/KpLoadingInline.svelte';
    import KpGenericPanel from '../../ui-components/panel/KpGenericPanel.svelte';
    import {fade} from 'svelte/transition';
    import {skipWhile} from 'rxjs/operators';

    export let record: Record = null;

    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);
    const CONTEXT_PATH: string = getContext(CONTEXT_PATH_KEY);
    const injector: Injector = getContext(INJECTOR_KEY);
    const presenter: KpFavouritesPanelPresenter = injector.getByToken(KpFavouritesPanelPresenter.presenterName);

    const limit = presenter.getLimit();

    let isInitialized = false;
    let size = 0;
    let showAddAll = false;
    let isLoading = true;
    let isEmpty = true;
    let records: RichRecord[] = [];

    // subscribe for updates after initial load in case already loaded items are not valid (e.g. incorrect sorting order)
    const favouriteRecordsSubscription = presenter.getFavouriteRecord$().pipe(skipWhile(() => !isInitialized)).subscribe((updatedRecordList) => updateRecordList(updatedRecordList));
    const statesSubscription = presenter.getFavouritesState$().subscribe((state) => updateLoadingState(state));
    const showAddAllVisibilitySubscription = presenter.getAddAllCapabilityChange$().subscribe(() => updateAddAllButtonVisibility());

    onMount(async () => {
        records = await presenter.loadFavourites();
        isInitialized = true;
    })

    onDestroy(() => {
        favouriteRecordsSubscription.unsubscribe();
        statesSubscription.unsubscribe();
        showAddAllVisibilitySubscription.unsubscribe();
    })

    function updateLoadingState(state: State) {
        isLoading = state === states.PROCESSING || state === states.INITIAL;
        if (state === states.NORMAL) {
            size = presenter.getSize();
            isEmpty = presenter.isEmpty();
        }
    }

    function updateRecordList(updatedRecordList: RichRecord[]) {
        records = updatedRecordList;
    }

    function updateAddAllButtonVisibility() {
        showAddAll = presenter.canAddPotentials();
    }

    function addAll() {
        presenter.addAll();
    }

    function removeAll() {
        presenter.removeAll();
    }

</script>

<template>
    <!-- id serves as scrollTo target for $anchorScroll -->
    <section class="oblibene-container" id="favourites-panel" aria-labelledby="favourites-panel-label">
        <KpGenericPanel>
            <svelte:fragment slot="heading">
                {#if !!record}
                    <div class="pull-right">
                        <KpFavouritesButton {record}/>
                    </div>
                {/if}
                {#if showAddAll}
                    <div class="pull-right">
                        <button type="button"
                                use:disabledWhen={isLoading}
                                on:click={addAll}
                                class="btn btn-xs btn-default">
                            {localize(/* @kp-localization recordCollections.favourites.addAllFromSearch */ 'recordCollections.favourites.addAllFromSearch')}
                        </button>
                    </div>
                {/if}

                <h2 id="favourites-panel-label" class="unset-style">
                    {localize(/* @kp-localization recordCollections.favourites.Favourites */ 'recordCollections.favourites.Favourites')}
                </h2>
                <span class="badge" class:jp-hidden={isLoading}>{size}</span>
            </svelte:fragment>

            {#if !isEmpty}
                <div>
                    <div class="covers-list">
                        {#each records as item (item.id)}
                            <div class="cover-container-in-mini-list" transition:fade|local={{duration:200}}>
                                <KpCover record={item} fillTo="width"/>
                            </div>
                        {:else}
                            <!-- if favourites is not empty but records have not yet been loaded (size of the actual list is 0) -->
                            <KpLoadingBlock size="sm"/>
                        {/each}
                        {#if (size > limit)}
                            <div class="and-more-message-text">&hellip;{localize(/* @kp-localization commons.aDalsi */ 'commons.aDalsi')}</div>
                        {/if}
                    </div>

                    <hr/>

                    <a href="{CONTEXT_PATH}/#!/favourites" class="btn btn-default btn-xs">{localize(/* @kp-localization commons.ZobrazitVse */ 'commons.ZobrazitVse')}</a>

                    <button type="button"
                            class="btn btn-default btn-xs"
                            use:disabledWhen={isLoading}
                            on:click={removeAll}>
                        {localize(/* @kp-localization recordCollections.removeAll */ 'recordCollections.removeAll')}
                    </button>
                    <span class="pull-right">
                        <KpLoadingInline showWhen={isLoading} size="xs"/>
                    </span>
                </div>
            {:else}
                <div>
                    <span class:jp-hidden={isLoading}>
                        {localize(/* @kp-localization commons.ZadnePolozky */ 'commons.ZadnePolozky')}
                    </span>
                    <span class="pull-right">
                        <KpLoadingInline showWhen={isLoading} size="xs"/>
                    </span>
                </div>

            {/if}
        </KpGenericPanel>
    </section>
</template>

<style lang="less">
  .covers-list {
    display: flex;
  }

  .cover-container-in-mini-list {
    width: 23px;
    margin-right: 2px;
  }

  .and-more-message-text {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
</style>